// AgentVerse/client/ui/UserConfigurationView.js
import { ComponentBase } from "../core/ComponentBase.js";
import { avatarHelper } from "../../helpers/avatarHelper.js";

export class UserConfigurationView extends ComponentBase {
    static styles = `
        /* Styles can be similar to AgentConfigurationView for consistency */
        .user-config-view {
            padding: 1rem;
            height: 100%;
            overflow-y: auto;
        }
        .config-header {
            display: flex;
            align-items: center;
            gap: 1.5rem;
            padding-bottom: 1rem;
            margin-bottom: 1rem;
            border-bottom: 1px solid var(--bs-border-color);
        }
        .user-avatar-large {
            width: 96px; height: 96px;
        }
        .config-section {
            background-color: var(--bs-tertiary-bg);
            border-radius: var(--ab-radius-sm);
            padding: 1rem;
            margin-bottom: 1.5rem;
        }
        .config-section h5 {
            margin-top: 0;
            border-bottom: 1px solid var(--bs-border-color-translucent);
            padding-bottom: 0.5rem;
            margin-bottom: 1rem;
        }
        .disabled-section {
            opacity: 0.65;
            pointer-events: none;
        }
        .disabled-section .form-text {
            display: block;
        }
        @media (max-width: 768px) {
            .config-header {
                flex-direction: column;
                align-items: flex-start;
                gap: 1rem;
            }
            .config-header .ms-auto {
                margin-left: 0 !important;
                width: 100%;
                display: flex;
                gap: 0.5rem;
            }
            .config-header .ms-auto .btn {
                flex: 1;
            }
        }
    `;

    constructor(config) {
        super(config);
        this.userId = null;
        this.userData = {};
        this.telephonyData = { extensions: [], availableExtensions: [] };
        this.availableRoles = [];
        this.isCreateMode = false;
        this.applyStyles();
    }

    async initAfterLogin() {
        try {
            this.availableRoles = await this.appClient.rpcClient.call('RoleRpc.listRoles') || [];
        } catch (e) { this.log.error("Failed to fetch roles:", e) }
    }

    // async onShow() {
    //     await this.loadDataBasedOnRoute();
    // }

    async refresh() {
        await this.loadDataBasedOnRoute();
    }

    async loadDataBasedOnRoute() {
        const queryParams = this.appClient.layoutEngine.getQueryParams();
        const userIdFromQuery = queryParams.userId;

        if (!userIdFromQuery) {
            this.log.info("UserConfigurationView: No userId, entering CREATE mode.");
            this.userId = null;
            this.isCreateMode = true;
            this.userData = { username: '', email: '', firstName: '', lastName: '', role: 'user', isActive: true };
            this.telephonyData = { extensions: [], availableExtensions: [] };
            if (this.container) await this.render();
            return;
        }

        if (this.userId !== userIdFromQuery || this.isCreateMode) { // Relaod if switching from create to edit
            this.isCreateMode = false;
            this.userId = userIdFromQuery;
            await this.loadUserData();
            await this.loadTelephonyData();
        }

        if (this.container) await this.render();
    }

    async loadUserData() {
        if (!this.userId) return;
        try {
            this.userData = await this.appClient.rpcClient.call('UserManagerRpc.findById', { _id: this.userId });
        } catch (error) {
            this.log.error("Error loading user data:", error);
            this.appClient.notifier.error("Failed to load user details.");
            this.userData = {};
        }
    }

    async loadTelephonyData() {
        if (!this.userId || this.isCreateMode) {
            this.telephonyData = { extensions: [], availableExtensions: [] };
            return;
        }
        try {
            console.log("[UserConfigurationView] loadTelephonyData fetching for appUserId:", this.userId);
            // --- FIX: NO LONGER PASSING PROJECT CONTEXT ---
            const assignedExt = await this.appClient.rpcClient.call('TelephonyManagerRpc.getExtensionsForUser', {
                appUserId: this.userId
            });
            console.log("[UserConfigurationView] loadTelephonyData result for assignedExt:", assignedExt);
            this.telephonyData.extensions = assignedExt || [];

            // --- FIX: CALL THE NEW GLOBAL METHOD ---
            const availableExt = await this.appClient.rpcClient.call('TelephonyManagerRpc.getAvailableExtensions');
            this.telephonyData.availableExtensions = availableExt || [];

            this.log.info("Telephony data for user loaded (globally):", this.telephonyData);
        } catch (error) {
            this.log.error("Error loading telephony data for user:", error);
        }
    }


    async render() {
        // ... (render method is unchanged) ...
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'user-config-view';
        }
        this.container.innerHTML = '';

        if (!this.userId && !this.isCreateMode) {
            this.container.innerHTML = `<p>${this.t('Loading user configuration...')}</p>`;
            return this.container;
        }

        const header = this.renderHeader();
        const detailsSection = this.renderDetailsSection();
        const phoneSection = this.renderPhoneSection();

        this.container.appendChild(header);
        this.container.appendChild(detailsSection);
        this.container.appendChild(phoneSection);

        this.attachEventListeners();
        this.appClient.tx.applyTranslations(this.container);
        return this.container;
    }

    renderHeader() {
        // ... (renderHeader method is unchanged) ...
        const header = document.createElement('div');
        header.className = 'config-header';

        const avatarElement = avatarHelper.createAvatarElement({
            displayName: this.isCreateMode ? '?' : this.userData.username,
            avatarUrl: this.isCreateMode ? null : this.userData.profilePicture,
            sizeClass: 'large',
            additionalClasses: ['user-avatar-large']
        });

        const headerInfo = document.createElement('div');
        headerInfo.innerHTML = `
            <h2>${this.isCreateMode ? this.t('New User') : this.userData.username}</h2>
            <p class="text-muted">${this.isCreateMode ? this.t('Enter user details below') : this.userData.email}</p>
        `;

        const saveButtonLabel = this.isCreateMode ? this.t('Create User') : this.t('Save Changes');

        const headerActions = document.createElement('div');
        headerActions.className = 'ms-auto';
        headerActions.innerHTML = `
            <button class="btn btn-secondary" id="back-to-list-btn"><i class="bi bi-arrow-left"></i> ${this.t('Back to List')}</button>
            <button class="btn btn-primary" id="save-all-changes-btn"><i class="bi bi-save"></i> ${saveButtonLabel}</button>
        `;
        header.appendChild(avatarElement);
        header.appendChild(headerInfo);
        header.appendChild(headerActions);
        return header;
    }

    renderDetailsSection() {
        const section = document.createElement('form');
        section.setAttribute('onsubmit', 'return false;');
        section.setAttribute('autocomplete', 'off');
        section.className = 'config-section';
        const roleOptions = this.availableRoles.map(r => `<option value="${r.name}" ${this.userData.role === r.name ? 'selected' : ''}>${this.t(r.name)}</option>`).join('');

        // --- PASSWORD FIELD IMPROVEMENT ---
        // Conditionally set label and placeholder based on mode
        const passwordLabel = this.isCreateMode ? this.t('Password') : this.t('New Password');
        const passwordPlaceholder = this.isCreateMode ? '' : this.t('Leave blank to keep unchanged');
        const passwordRequired = this.isCreateMode ? 'required' : '';

        const passwordFieldHtml = `
            <div class="col-md-6 mb-3">
                <label for="user-password" class="form-label">${passwordLabel}</label>
                <input type="password" id="user-password" class="form-control" autocomplete="new-password" 
                       placeholder="${passwordPlaceholder}" ${passwordRequired}>
            </div>
        `;
        // --- END IMPROVEMENT ---

        section.innerHTML = `
            <h5>User Details & Role</h5>
            <div class="row">
                <div class="col-md-6 mb-3">
                    <label for="user-username" class="form-label">${this.t('Username')}</label>
                    <input type="text" id="user-username" class="form-control" value="${this.userData.username || ''}" required>
                </div>
                <div class="col-md-6 mb-3">
                    <label for="user-email" class="form-label">${this.t('Email')}</label>
                    <input type="email" id="user-email" class="form-control" value="${this.userData.email || ''}" required>
                </div>
                ${passwordFieldHtml}
                <div class="col-md-6 mb-3">
                    <label for="user-firstName" class="form-label">${this.t('First Name')}</label>
                    <input type="text" id="user-firstName" class="form-control" value="${this.userData.firstName || ''}">
                </div>
                <div class="col-md-6 mb-3">
                    <label for="user-lastName" class="form-label">${this.t('Last Name')}</label>
                    <input type="text" id="user-lastName" class="form-control" value="${this.userData.lastName || ''}">
                </div>
                <div class="col-md-6 mb-3">
                    <label for="user-role" class="form-label">${this.t('Role')}</label>
                    <select id="user-role" class="form-select">${roleOptions}</select>
                </div>
                 <div class="col-md-6 mb-3 d-flex align-items-end">
                    <div class="form-check">
                        <input class="form-check-input" type="checkbox" id="user-isActive" ${this.userData.isActive ? 'checked' : ''}>
                        <label class="form-check-label" for="user-isActive">${this.t('Is Active')}</label>
                    </div>
                </div>
            </div>
        `;
        return section;
    }

    renderPhoneSection() {
        // ... (renderPhoneSection method is unchanged) ...
        const section = document.createElement('div');
        section.className = 'config-section';
        if (this.isCreateMode) {
            section.classList.add('disabled-section');
        }

        section.innerHTML = `
            <h5>Phone & Extension</h5>
            <div class="mb-2">
                <label class="form-label">${this.t('Assigned Extension:')} <strong id="assigned-extension-display">${this.telephonyData.extensions.length > 0 ? this.telephonyData.extensions[0].extension : this.t('Not Assigned')}</strong></label>
                <button class="btn btn-sm btn-outline-secondary ms-2" id="manage-extensions-btn">${this.t('Manage')}</button>
            </div>
            ${this.isCreateMode ? `<small class="form-text text-muted">${this.t('Save the user first to assign an extension.')}</small>` : ''}
        `;
        return section;
    }

    attachEventListeners() {
        // ... (attachEventListeners method is unchanged) ...
        this.container.querySelector('#back-to-list-btn')?.addEventListener('click', () => {
            this.appClient.layoutEngine.navigate('/admin/users');
        });
        this.container.querySelector('#save-all-changes-btn')?.addEventListener('click', this.handleSaveChanges.bind(this));
        this.container.querySelector('#manage-extensions-btn')?.addEventListener('click', this.showManageExtensionsModal.bind(this));
    }

    async handleSaveChanges() {
        try {
            if (this.isCreateMode) {
                // --- CREATE LOGIC ---
                const createData = {
                    username: this.container.querySelector('#user-username').value,
                    email: this.container.querySelector('#user-email').value,
                    password: this.container.querySelector('#user-password').value,
                    firstName: this.container.querySelector('#user-firstName').value,
                    lastName: this.container.querySelector('#user-lastName').value,
                    role: this.container.querySelector('#user-role').value,
                    isActive: this.container.querySelector('#user-isActive').checked,
                };
                if (!createData.password) { // Password is required for creation
                    this.appClient.notifier.error("Password is required to create a new user.");
                    return;
                }
                const createdUser = await this.appClient.rpcClient.call('UserManagerRpc.create', createData);
                this.appClient.notifier.success(`User "${createdUser.username}" created successfully!`);
                this.appClient.layoutEngine.navigate(`/admin/user-config?userId=${createdUser._id}`);

            } else {
                // --- UPDATE LOGIC (MODIFIED) ---
                const updatedData = {
                    _id: this.userId,
                    username: this.container.querySelector('#user-username').value,
                    email: this.container.querySelector('#user-email').value,
                    firstName: this.container.querySelector('#user-firstName').value,
                    lastName: this.container.querySelector('#user-lastName').value,
                    role: this.container.querySelector('#user-role').value,
                    isActive: this.container.querySelector('#user-isActive').checked,
                };

                // Only include the newPassword field if it's been filled out
                const newPassword = this.container.querySelector('#user-password').value;
                if (newPassword && newPassword.trim() !== '') {
                    updatedData.newPassword = newPassword;
                }

                await this.appClient.rpcClient.call('UserManagerRpc.update',{ userId: this.userId, updates: updatedData});
                this.appClient.notifier.success("User details saved successfully!");
                await this.loadUserData();
                await this.render();
            }
        } catch (error) {
            this.log.error("Error saving user:", error);
            this.appClient.notifier.error(`Save failed: ${error.message}`);
        }
    }

    async showManageExtensionsModal() {
        const modalId = `${this.config.instanceId}-manageUserExtModal`;
        document.getElementById(modalId)?.remove();

        const currentAssignedExt = this.telephonyData.extensions.length > 0 ? this.telephonyData.extensions[0].extension : null;
        let optionsHtml = `<option value="">-- ${this.t('Select Extension')} --</option>`;
        this.telephonyData.availableExtensions.forEach(ext => {
            optionsHtml += `<option value="${ext}">${ext}</option>`;
        });

        const modalHtml = `
            <div class="modal fade" id="${modalId}" tabindex="-1"> <div class="modal-dialog modal-dialog-centered"> <div class="modal-content">
                <div class="modal-header"><h5 class="modal-title">${this.t('Manage User Extension')}</h5><button type="button" class="btn-close" data-bs-dismiss="modal"></button></div>
                <div class="modal-body">
                    <div class="mb-3"><label for="extension-select" class="form-label">${this.t('Available Extensions')}</label><select class="form-select" id="extension-select">${optionsHtml}</select></div>
                    ${currentAssignedExt ? `<p>${this.t('Current extension:')} <strong>${currentAssignedExt}</strong></p>` : `<p>${this.t('No extension currently assigned.')}</p>`}
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">${this.t('Cancel')}</button>
                    ${currentAssignedExt ? `<button type="button" class="btn btn-danger" id="disassociate-ext-btn">${this.t('Disassociate Current')}</button>` : ''}
                    <button type="button" class="btn btn-primary" id="assign-ext-btn">${this.t('Assign Selected')}</button>
                </div></div></div></div>
        `;
        document.body.insertAdjacentHTML('beforeend', modalHtml);
        const modalElement = document.getElementById(modalId);
        const bsModal = new bootstrap.Modal(modalElement);
        const context = { projectId: this.appClient.getSharedData('selectedProjectId') };

        modalElement.querySelector('#assign-ext-btn').addEventListener('click', async () => {
            const selectedExtension = modalElement.querySelector('#extension-select').value;
            if (selectedExtension) {
                try {
                    const assignPayload = {
                        appUserId: this.userId,
                        extension: selectedExtension
                    };
                    console.log("[UserConfigurationView] Calling assignUserExtension with payload:", assignPayload);
                    const assignResult = await this.appClient.rpcClient.call('TelephonyManagerRpc.assignUserExtension', assignPayload);
                    console.log("[UserConfigurationView] assignUserExtension returned:", assignResult);

                    this.appClient.notifier.success(`Extension ${selectedExtension} assigned.`);
                    await this.loadTelephonyData();
                    this.render(); // Re-render the whole view to update display
                    bsModal.hide();

                } catch (error) { this.appClient.notifier.error(`Failed to assign: ${error.message}`); }
            }
        });

        if (currentAssignedExt) {
            modalElement.querySelector('#disassociate-ext-btn').addEventListener('click', async () => {
                try {
                    await this.appClient.rpcClient.call('TelephonyManagerRpc.disassociateUserExtension', {
                        appUserId: this.userId,
                        extension: currentAssignedExt
                    });
                    this.appClient.notifier.success(`Extension ${currentAssignedExt} disassociated.`);
                    await this.loadTelephonyData();
                    this.render();
                    bsModal.hide();
                } catch (error) { this.appClient.notifier.error(`Failed to disassociate: ${error.message}`); }
            });
        }
        this.appClient.tx.applyTranslations(modalElement);
        bsModal.show();
        modalElement.addEventListener('hidden.bs.modal', () => modalElement.remove(), { once: true });
    }
}